import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, orderBy, query } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { repairTruckConverter, shipmentPlanConverter, transportTruckConverter } from '@otw/models/converters'
import {
  getRepairTruckWorkStatus,
  getTruckStatus,
  hasIncompleteRepairShopsOnPlan,
  type RepairTruck,
  type ShipmentPlan,
  type TransportTruck,
} from '@otw/models/logistics'
import { getLatestShipmentPlan } from '@/hooks/logistics/useLogistics'

/**
 * Same ready-for-sale derivation as the AG Grid inventory. Reads the same React
 * Query keys as `useLogistics` so opening v3 does not issue a second getDocs of
 * transport / repair / shipment collections.
 */
export const useReadyForSaleVehicleIds = () => {
  const { data: transportTrucks = [] } = useQuery({
    queryKey: ['transport_trucks'],
    queryFn: async (): Promise<TransportTruck[]> => {
      const transportCollection = collection(db, 'transport_trucks').withConverter(transportTruckConverter)
      const snapshot = await getDocs(query(transportCollection, orderBy('created_at', 'desc')))
      return snapshot.docs.map(docSnap => docSnap.data())
    },
  })

  const { data: repairTrucks = [] } = useQuery({
    queryKey: ['repair_trucks', 'all'],
    queryFn: async (): Promise<RepairTruck[]> => {
      const repairCollection = collection(db, 'repair_trucks').withConverter(repairTruckConverter)
      const snapshot = await getDocs(repairCollection)
      return snapshot.docs.map(docSnap => docSnap.data())
    },
  })

  const { data: shipmentPlans = [] } = useQuery({
    queryKey: ['shipment_plans'],
    queryFn: async (): Promise<ShipmentPlan[]> => {
      const plansCollection = collection(db, 'shipment_plans').withConverter(shipmentPlanConverter)
      const snapshot = await getDocs(query(plansCollection, orderBy('created_at', 'desc')))
      return snapshot.docs.map(docSnap => docSnap.data())
    },
  })

  return useMemo(() => {
    const latestRepairByTransportTruckId = new Map<string, { repairTruck: RepairTruck; createdAtMs: number }>()
    const repairTrucksByTransportTruckId = new Map<string, RepairTruck[]>()
    repairTrucks.forEach(repairTruck => {
      const transportTruckId = repairTruck.transport_truck_id
      if (!transportTruckId) return
      const createdAtMs = repairTruck.created_at?.toMillis?.() ?? 0
      const existing = latestRepairByTransportTruckId.get(transportTruckId)
      if (!existing || createdAtMs >= existing.createdAtMs) {
        latestRepairByTransportTruckId.set(transportTruckId, { repairTruck, createdAtMs })
      }
      const list = repairTrucksByTransportTruckId.get(transportTruckId) ?? []
      list.push(repairTruck)
      repairTrucksByTransportTruckId.set(transportTruckId, list)
    })

    const plansByTransportTruckId = new Map<string, ShipmentPlan[]>()
    shipmentPlans.forEach(plan => {
      const transportTruckId = plan.transport_truck_id
      if (!transportTruckId) return
      const list = plansByTransportTruckId.get(transportTruckId) ?? []
      list.push(plan)
      plansByTransportTruckId.set(transportTruckId, list)
    })

    const matchingVehicleIds = new Set<string>()
    transportTrucks.forEach(transportTruck => {
      const latestRepair = latestRepairByTransportTruckId.get(transportTruck.id)?.repairTruck
      if (!latestRepair) return
      const truckStatus = getTruckStatus(transportTruck)
      if (truckStatus !== 'repair' && truckStatus !== 'awaiting_transport') return
      if (!transportTruck.currentLocationId) return
      if (transportTruck.currentLocationId !== latestRepair.repair_shop_location_id) return
      if (getRepairTruckWorkStatus(latestRepair) !== 'complete') return

      const latestPlan = getLatestShipmentPlan(plansByTransportTruckId.get(transportTruck.id) ?? [])
      const repairsForTruck = repairTrucksByTransportTruckId.get(transportTruck.id) ?? []
      if (hasIncompleteRepairShopsOnPlan(latestPlan, repairsForTruck)) return
      if (!transportTruck.vehicle_id) return
      matchingVehicleIds.add(transportTruck.vehicle_id)
    })

    return matchingVehicleIds
  }, [transportTrucks, repairTrucks, shipmentPlans])
}
